import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatKobo } from "../../lib/format";
import { useSettings } from "../../context/SettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const STATUSES = ["Pending Payment", "Paid", "Confirmed", "Preparing", "Out for Delivery", "Delivered", "Cancelled", "Refunded"];
const CHIPS = ["all", "Open", ...STATUSES];
const METRIC_LABELS = { orders_today: "Orders today", paid_today: "Paid today", open: "Open orders" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showTest, setShowTest] = useState(false);
  const [selected, setSelected] = useState(null);
  const [eta, setEta] = useState("");
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const metric = searchParams.get("metric");

  const load = useCallback(() => {
    const params = { include_test: showTest };
    if (metric) params.metric = metric;
    else if (statusFilter === "Open") params.status = "open";
    else if (statusFilter !== "all") params.status = statusFilter;
    if (q) params.q = q;
    api.get("/admin/orders", { params }).then(({ data }) => setOrders(data)).catch(() => {});
  }, [statusFilter, q, metric, showTest]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const pickChip = (s) => {
    if (metric) setSearchParams({});
    setStatusFilter(s);
  };

  const activeLabel = metric ? (METRIC_LABELS[metric] || "Orders") : (statusFilter === "all" ? "All orders" : statusFilter === "Open" ? "Open orders" : statusFilter);

  const changeStatus = async (order, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      if (selected?.id === order.id) setSelected(data);
      toast.success(`Status set to ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEta = async () => {
    if (!eta.trim()) return toast.error("Enter an estimated time");
    try {
      const { data } = await api.patch(`/admin/orders/${selected.id}/eta`, { eta });
      setOrders((prev) => prev.map((o) => (o.id === selected.id ? data : o)));
      setSelected(data);
      toast.success("ETA saved & customer emailed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = (o) => { setSelected(o); setEta(o.eta || ""); };

  const waReply = (o) => {
    const num = (o.customer.phone || "").replace(/[^0-9]/g, "").replace(/^0/, "234");
    const msg = encodeURIComponent(`Hello ${o.customer.name}, your Sapore Eats order ${o.reference} is confirmed. Estimated delivery time: ${o.eta || "shortly"}. Thank you!`);
    return `https://wa.me/${num}?text=${msg}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl font-bold text-sapore-purple">{activeLabel} <span className="text-sapore-muted text-xl">({orders.length})</span></h1>
        <label className="flex items-center gap-2 text-sm text-sapore-inkSoft">
          <input type="checkbox" data-testid="show-test-toggle" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} className="w-4 h-4 accent-sapore-purple" />
          Show test data
        </label>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex gap-2 overflow-x-auto hide-scroll">
          {CHIPS.map((s) => {
            const active = !metric && statusFilter === s;
            return (
              <button key={s} data-testid={`order-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => pickChip(s)} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-sapore-purple text-sapore-cream border-sapore-purple" : "bg-white border-sapore-border text-sapore-ink"}`}>
                {s === "all" ? "All" : s}
              </button>
            );
          })}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sapore-muted" />
          <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference or phone" className="w-full pl-9 pr-3 py-2 rounded-full border border-sapore-border bg-white text-sm focus:outline-none" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-sapore-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sapore-creamSurface">
              <tr className="text-left text-sapore-muted">
                <th className="py-3 px-4">Reference</th><th className="py-3 px-4">Customer</th><th className="py-3 px-4">Total</th><th className="py-3 px-4">Payment</th><th className="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.reference}`} onClick={() => openDetail(o)} className="border-t border-sapore-border/60 hover:bg-sapore-goldSubtle/40 cursor-pointer">
                  <td className="py-3 px-4 font-medium">{o.reference}{o.is_test && <span data-testid={`test-badge-${o.reference}`} className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">TEST</span>}</td>
                  <td className="py-3 px-4">{o.customer?.name}<span className="block text-xs text-sapore-muted">{o.customer?.phone}</span></td>
                  <td className="py-3 px-4">{formatKobo(o.total)}</td>
                  <td className="py-3 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span></td>
                  <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded-full bg-sapore-goldSubtle text-sapore-ink">{o.status}</span></td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-sapore-muted">No orders found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" data-testid="order-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-sapore-purple">{selected.reference}</DialogTitle>
              </DialogHeader>
              <div className="text-sm space-y-1 text-sapore-inkSoft">
                <p><strong className="text-sapore-ink">{selected.customer.name}</strong> · {selected.customer.phone}</p>
                <p>{selected.customer.email}</p>
                <p>{selected.customer.address} — {selected.zone_name}</p>
                {selected.notes && <p className="italic">Note: {selected.notes}</p>}
                {selected.paystack_ref && <p className="text-xs text-sapore-muted">Payment ref: {selected.paystack_ref}</p>}
              </div>
              {selected.allergy_flag ? (
                <div className="mt-3 rounded-lg bg-red-600 text-white px-3 py-2" data-testid="order-detail-allergy">
                  <p className="font-bold text-sm">⚠ ALLERGY — {(selected.allergy_tags || []).join(", ")}</p>
                  {selected.allergy_note && <p className="text-xs text-white/90 mt-0.5">{selected.allergy_note}</p>}
                </div>
              ) : (
                <p className="mt-3 text-xs text-sapore-muted" data-testid="order-detail-allergy">No allergies noted.</p>
              )}
              <ul className="mt-3 border-t border-sapore-border pt-3 space-y-2 text-sm">
                {selected.items.map((it, i) => (
                  <li key={`${it.item_id || it.name}-${i}`} className="flex justify-between">
                    <span>{it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}
                      {it.addons?.length > 0 && <span className="block text-xs text-sapore-muted">+ {it.addons.map((a) => a.name).join(", ")}</span>}
                      {it.special_instructions && <span className="block text-xs text-sapore-muted italic">"{it.special_instructions}"</span>}
                    </span>
                    <span className="font-medium">{formatKobo(it.line_total)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-sapore-border mt-3 pt-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatKobo(selected.subtotal)}</span></div>
                <div className="flex justify-between"><span>Delivery</span><span>{formatKobo(selected.delivery_fee)}</span></div>
                <div className="flex justify-between font-bold"><span>Total</span><span className="text-sapore-gold">{formatKobo(selected.total)}</span></div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-sapore-ink mb-1.5">Status</label>
                <select data-testid="order-status-select" value={selected.status} onChange={(e) => changeStatus(selected, e.target.value)} className="w-full rounded-xl border border-sapore-border p-2.5 text-sm bg-white">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="mt-4 bg-sapore-creamSurface rounded-xl p-4">
                <label className="flex items-center gap-2 text-sm font-medium text-sapore-ink mb-2"><Clock size={16} /> Estimated delivery time</label>
                <div className="flex gap-2">
                  <input data-testid="order-eta-input" value={eta} onChange={(e) => setEta(e.target.value)} placeholder="e.g. 45 minutes" className="flex-1 rounded-xl border border-sapore-border p-2.5 text-sm" />
                  <button data-testid="order-eta-save" onClick={saveEta} className="px-4 rounded-xl bg-sapore-purple text-sapore-cream text-sm font-medium hover:bg-sapore-purpleDark">Save & notify</button>
                </div>
                <a href={waReply(selected)} target="_blank" rel="noreferrer" data-testid="order-whatsapp-reply" className="inline-flex items-center gap-2 mt-3 text-sm text-sapore-whatsapp font-medium hover:underline">
                  <Send size={14} /> WhatsApp the customer
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
